'use client'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FileText, ExternalLink, Eye } from 'lucide-react'

interface HsVersionHistoryModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  hsCode: string
}

const CASELAW_BASE_URL = process.env.NEXT_PUBLIC_CASELAW_BASE_URL || 'https://caselaw.vn'

export default function HsVersionHistoryModal({
  open,
  onOpenChange,
  hsCode,
}: HsVersionHistoryModalProps) {
  // Clean hsCode for URL (remove dashes)
  const cleanHsCode = hsCode.replace(/-/g, '')
  const archiveUrl = `${CASELAW_BASE_URL}/luutru/chi-tiet-ma-hs/${cleanHsCode}`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] w-full p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 py-5 border-b-2 border-neutral-100">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2.5 text-base font-bold">
                <span className="flex items-center justify-center size-6 p-1 bg-blue-600 rounded-md text-white">
                  <FileText className="size-4" />
                </span>
                <span>Lịch sử phiên bản mã HS {hsCode}</span>
              </DialogTitle>
              <p className="mt-2.5 pl-8 text-[13px] text-neutral-600 leading-relaxed">
                Thông tin mã HS đã được cập nhật theo các biểu thuế khác nhau qua từng giai đoạn.
                Bạn có thể tra cứu phiên bản phù hợp đối với mã HS này.
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Body - Version Table */}
        <table className="w-full">
          {/* Table Header */}
          <thead className="bg-neutral-50">
            <tr className="text-[11px] font-bold uppercase tracking-wide text-neutral-600">
              <th className="px-4 py-2.5 text-left font-bold">Phiên bản áp dụng</th>
              <th className="px-4 py-2.5 text-left font-bold">Giai đoạn</th>
              <th className="px-4 py-2.5 text-left font-bold">Trạng thái</th>
              <th className="px-4 py-2.5 text-left font-bold"></th>
            </tr>
          </thead>

          <tbody>
            {/* Current Version Row */}
            <tr className="border-b border-neutral-100">
              <td className="px-4 py-4 text-[15px] font-semibold text-neutral-900">
                Thông tư 31/2022/TT-BTC
              </td>
              <td className="px-4 py-4 text-sm text-neutral-700">
                Từ 30/12/2022 - Hiện tại
              </td>
              <td className="px-4 py-4">
                <span className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-blue-600 text-white">
                  Hiện hành
                </span>
              </td>
              <td className="px-4 py-4">
                <span className="flex items-center gap-1.5 text-sm font-medium text-blue-600">
                  <Eye className="size-4 text-blue-600" />
                  <span>Đang xem</span>
                </span>
              </td>
            </tr>

            {/* Archived Version Row */}
            <tr>
              <td className="px-4 py-4 text-[15px] font-semibold text-neutral-900">
                Thông tư 65/2017/TT-BTC
              </td>
              <td className="px-4 py-4 text-sm text-neutral-700">
                15/06/2017 - 29/12/2022
              </td>
              <td className="px-4 py-4">
                <span className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-neutral-200 text-neutral-700">
                  Lưu trữ
                </span>
              </td>
              <td className="px-4 py-4">
                <a
                  href={archiveUrl}
                  target="_blank"
                  className="flex items-center gap-1.5 text-sm font-medium text-neutral-700 hover:text-blue-600 transition-colors"
                >
                  <ExternalLink className="size-4 text-blue-600" />
                  <span>Xem</span>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  )
}